import { defineAsyncComponent } from 'vue';
import { gql } from '@apollo/client/core';
import { executeQuery } from '@/components/commons/utils.js';
import LocationService from './LocationService';
import MemoryCache from './MemoryCache';

const dependencyMap = {
  productCarousel: ['CanFavorite'],
  productGrid: ['CanFavorite'],
  wrappedComponent: {
    Recommender: ['CanFavorite'],
    RoomsIdeas: ['ProductListModal'],
    Reviews: ['CanFavorite'],
  },
};

const dependencyModuleMap = {
  CanFavorite: defineAsyncComponent(() => import('@/components/shared/CanFavorite.vue')),
  ProductListModal: defineAsyncComponent(() => import('@/components/commons/ShopByWidget/components/ProductListModal.vue')),
};

export function getContentfulComponentDependencies(contentType, component = '') {
  if (contentType == 'wrappedComponent') {
    return dependencyMap[contentType][component] ?? [];
  } else {
    return dependencyMap[contentType] ?? [];
  }
}

export function getComponentDependencyModules(moduleNames) {
  return moduleNames.map((module) => dependencyModuleMap[module] ?? null);
}

const contentTypeMap = {
  imageCarousel: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulAssetCarousel.vue')),
  pageComponentBigQuote: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulBigQuote.vue')),
  pageComponentForm: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulTradeForm.vue')),
  pageComponentCtaButton: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulCTAButton.vue')),
  pageComponentSection: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulSection.vue')),
  pageComponentResponsiveAsset: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulResponsiveAsset.vue')),
  pageComponentImageTextCarousel: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulTileCarousel.vue')),
  pageComponentTile: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulTile.vue')),
  pageComponentSpacer: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulSpacer.vue')),
  campaignPage: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentPage.vue')),
  hero: defineAsyncComponent(() => import('@/components/contentful/ContentModels/Hero.vue')),
  gridRow: defineAsyncComponent(() => import('@/components/contentful/ContentModels/GridRow.vue')),
  wrappedComponent: defineAsyncComponent(() => import('@/components/contentful/ContentModels/WrappedComponent.vue')),
  ugc: defineAsyncComponent(() => import('@/components/contentful/ContentModels/Ugc.vue')),
  productCarousel: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulProductCarousel.vue')),
  valueProps: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulValueProps.vue')),
  experiment: defineAsyncComponent(() => import('@/components/contentful/ContentModels/Experiment.vue')),
  experimentVariant: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ExperimentVariant.vue')),
  productPage: defineAsyncComponent(() => import('@/components/product/ProductContentfulSection.vue')),
  bundleFeed: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulBundleCarousel.vue')),
  productGrid: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulProductGrid.vue')),
  table: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulTable.vue')),
  tabs: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulTabs.vue')),
  reviewsSection: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulReviews.vue')),
  bundlePage: defineAsyncComponent(() => import('@/pages/Sets/SetsPageGoesWellWith.vue')),
  relatedLinks: defineAsyncComponent(() => import('@/components/contentful/ContentModels/ContentfulRelatedLinks.vue')),
};

const BYKEY = gql`
  query CONTENTFUL_BY_ID($id: String!, $asset: Boolean, $depth: Number, $preview: Boolean, $locale: String) {
    contentfulById(id: $id, asset: $asset, depth: $depth, preview: $preview, locale: $locale) {
      root {
        sys {
          type
          id
          space {
            sys {
              id
            }
          }
          environment {
            sys {
              id
            }
          }
          contentType {
            sys {
              id
            }
          }
          updatedAt
          revision
        }
        fields
      }
      hints {
        sys {
          type
          id
          space {
            sys {
              id
            }
          }
          environment {
            sys {
              id
            }
          }
          contentType {
            sys {
              id
            }
          }
          updatedAt
          revision
        }
        fields
      }
    }
  }
`;

const BYMODELSLUG = gql`
  query CONTENTFUL_BY_MODEL_SLUG($model: String!, $slug: String!, $depth: Number, $preview: Boolean, $locale: String) {
    contentfulByModelSlug(model: $model, slug: $slug, depth: $depth, preview: $preview, locale: $locale) {
      root {
        sys {
          type
          id
          space {
            sys {
              id
            }
          }
          environment {
            sys {
              id
            }
          }
          contentType {
            sys {
              id
            }
          }
          updatedAt
          revision
        }
        fields
      }
      hints {
        sys {
          type
          id
          space {
            sys {
              id
            }
          }
          environment {
            sys {
              id
            }
          }
          contentType {
            sys {
              id
            }
          }
          updatedAt
          revision
        }
        fields
      }
    }
  }
`;

const cache = new MemoryCache(60000);

export function loadCacheFromJson(data, locale = 'en-US', preview = false) {
  addToCache(data.root, locale, preview);
  data.hints.map((e) => addToCache(e, locale, preview));
}

export function clearCache() {
  cache.cleanup();
}

export function getComponent(contentType) {
  if (!contentType) {
    return null;
  }
  if (contentTypeMap[contentType]) {
    return contentTypeMap[contentType];
  }
  logger.warn('Unknown Content Type', contentType);
  return null;
}

function addToCache(entry, locale, preview) {
  if (entry.sys?.id) {
    cache.set(formatCacheKeyForKey(entry.sys.id, locale, preview), entry);
  }
  const model = entry.sys?.contentType?.sys?.id;
  const slug = entry.fields?.slug;
  if (model && slug) {
    cache.set(formatCacheKeyForMS(model, slug, locale, preview), entry);
  }
}

function formatCacheKeyForKey(id, locale, preview) {
  return `key|${id}|${locale}|${preview}`;
}
function formatCacheKeyForMS(model, slug, locale, preview) {
  return `ms|${model}#${slug}|${locale}|${preview}`;
}

function getLocale() {
  return LocationService.isCanada() ? 'en-CA' : 'en-US';
}

function handleResults(document, locale, preview, _addToCache) {
  if (_addToCache) {
    if (document.hints && Array.isArray(document.hints)) {
      document.hints.map((e) => addToCache(e, locale, preview));
    }
    if (document.root) {
      addToCache(document.root, locale, preview);
    }
  }
  return document.root;
}

export async function getEntriesByKey(id, asset, depth = 1, preview = false) {
  const locale = getLocale();

  return cache.getOrCreate(formatCacheKeyForKey(id, locale, preview), async () => {
    const result = await executeQuery(
      BYKEY,
      {
        id,
        asset,
        depth,
        preview,
        locale,
      },
      {},
    );
    if (result.contentfulById) {
      const handled = handleResults(result.contentfulById, locale, preview, true);
      if (handled) {
        return handled;
      }
      logger.warn(`contentful entry ${id} returned no root`);
    } else {
      logger.warn(`contentful entry ${id} returned null`);
    }
    return null;
  });
}

export async function getEntriesByModelSlug(model, slug, depth = 1, preview = false, errorPolicy = 'none') {
  const locale = getLocale();
  return cache.getOrCreate(formatCacheKeyForMS(model, slug, locale, preview), async () => {
    const result = await executeQuery(
      BYMODELSLUG,
      {
        model,
        slug,
        depth,
        preview,
        locale,
      },
      {
        errorPolicy,
      },
    );

    if (result && result.contentfulByModelSlug) {
      const handled = handleResults(result.contentfulByModelSlug, locale, preview, true);
      if (handled) {
        return handled;
      }
      if (errorPolicy === 'none') {
        logger.warn(`contentful entry ${model}/${slug} returned no root`);
      }
    } else if (errorPolicy === 'none') {
      logger.warn(`contentful entry ${model}/${slug} returned null`);
    }
    return null;
  });
}

export async function resolveEntryMutating(entry, depth = 2, preview = false) {
  if (!entry) {
    return null;
  }
  if (entry.fields && Object.keys(entry.fields).length > 0) {
    return entry;
  }
  if (entry.sys?.id) {
    const response = await getEntriesByKey(entry.sys.id, entry.sys?.linkType === 'Asset', depth, preview);
    if (response.sys.id === entry.sys.id) {
      entry.fields = response.fields;
      entry.sys = { ...entry.sys, ...response.sys };
    }
    return entry;
  }
  logger.warn('not a valid entry', entry);
  return null;
}
